import * as React from "react";
import { stringify } from "qs";
import { useTranslation } from "react-i18next";
import { css } from "@emotion/react";
import { addDays, addMonths, format } from "date-fns";
import { FlightReferencePriceLink } from "main/javascripts/features/lp/flightDstRegion/components/FlightReferencePriceLink";
import { FlightTypes } from "main/javascripts/constants/FlightTypes";
import { LpFlightDstRegion } from "main/javascripts/types/lpFlightDstRegion";
import {
  destinationTypeForSearch,
  originTypeForSearch,
} from "../lpFlightDstRegionSlice";
import { maxWidth } from "main/javascripts/styles/base/maxWidthStyle";
import { space } from "main/javascripts/styles/base/spaceStyle";
import {
  accentColor,
  backgroundColor,
} from "main/javascripts/styles/base/colorStyle";
import {
  fontSize,
  fontWeight,
  lineHeight,
} from "main/javascripts/styles/base/typographyStyle";
import { moreThanBreakpoint } from "main/javascripts/styles/base/responsiveStyle";
import { useRouter } from "next/router";
import { useAppDispatch } from "main/javascripts/store";
import { unwrapResult } from "@reduxjs/toolkit";
import { flightAvailabilitiesEvent } from "main/javascripts/utils/googleTagManagerUtil";
// import {convertServerErrorToForm} from "main/javascripts/utils/FormUtil";
import { postSearchFlightCache } from "../../../flight/searchFlightSlice";

export interface IProps {
  originName: string;
  flightCheapestRoundTrips: LpFlightDstRegion.FlightCheapestRoundTrip[];
}

const dateFormat = "yyyy/LL/dd";

export const FlightPriceList: React.FC<IProps> = (
  props: IProps
): React.ReactElement => {
  const { originName, flightCheapestRoundTrips } = props;
  const { t } = useTranslation(["component"]);

  const router = useRouter();
  const appDispatch = useAppDispatch();

  const onClick: (originId: number) => void = async (originId: number) => {
    const today: Date = new Date();
    const departureDate: Date = addMonths(today, 2);
    const returnDate: Date = addDays(departureDate, 5);
    const targetFlightCheapest = flightCheapestRoundTrips.find(
      (flightCheapest) => flightCheapest.originId === originId
    );
    if (!targetFlightCheapest) {
      return;
    }
    const traveler: any = {
      number_of_adult: 2,
      number_of_children: 0,
    };
    const params: any = {
      origin_id: originId,
      origin: targetFlightCheapest.originName,
      origin_type: originTypeForSearch(targetFlightCheapest),
      destination_id: targetFlightCheapest.destinationId,
      destination: targetFlightCheapest.destinationName,
      destination_type: destinationTypeForSearch(targetFlightCheapest),
      cabin_class: "Y",
      departure_date: format(departureDate, dateFormat),
      return_date: format(returnDate, dateFormat),
      flight_type: FlightTypes.roundTrip,
      traveler: traveler,
    };
    return appDispatch(postSearchFlightCache({ params }))
      .then(unwrapResult)
      .then((result) => {
        const cacheId = result.data.cacheId;
        flightAvailabilitiesEvent(cacheId, params);
        router.push(
          `/flights?${stringify(params, {
            arrayFormat: "indices",
          })}&cache_id=${cacheId}`
        );
      })
      .catch((errorResponse) => {
        if (errorResponse.errors) {
          // エラー形式を合わせるために変換している
          // const errors = Object.keys(errorResponse.errors).map((key) => {
          //   return { [key]: errorResponse.errors[key] };
          // });
          alert(t("form.submitError"));
        }
      });
  };

  if (!flightCheapestRoundTrips || flightCheapestRoundTrips.length === 0) {
    return null;
  }
  return (
    <div css={blockStyle}>
      <h2 css={titleStyle}>
        {t("lp.flightReferencePriceListTitle", { destination: originName })}
      </h2>
      <div css={linkBlockStyle}>
        {flightCheapestRoundTrips.map((data, index: number) => {
          return (
            <FlightReferencePriceLink
              key={index}
              originName={data.originName}
              originId={data.originId}
              destinationName={data.destinationName}
              price={data.priceCents}
              priceCurrency={data.priceCurrency}
              onClick={onClick}
            />
          );
        })}
      </div>
    </div>
  );
};

const blockStyle = css`
  max-width: ${maxWidth.page1colWide};
  margin: 0 auto ${space.atom2x};
  box-sizing: border-box;
  padding: ${space.atom};
`;
const linkBlockStyle = css`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  margin: ${space.atom2x} 0 0;
  background: ${backgroundColor.secondaryLightColor};
`;
const titleStyle = css`
  font-size: ${fontSize.mediumHeading};
  line-height: ${lineHeight.headline1};
  color: ${accentColor.secondaryColor2};
  padding: ${space.atom};
  font-weight: ${fontWeight.bold};
  letter-spacing: 0.1em;
  margin: 0.5rem 0;
  position: relative;
  padding-bottom: ${space.atom2x};
  &:before {
    content: "";
    position: absolute;
    bottom: 0;
    display: block;
    height: 1px;
    width: calc(100% - ${space.atom2x});
    background: #ddd;
  }
  &:after {
    content: "";
    position: absolute;
    bottom: 0;
    display: block;
    height: 1px;
    width: 3rem;
    background: ${accentColor.primaryColor};
  }
  ${moreThanBreakpoint("tablet")} {
    font-size: ${fontSize.headline};
  }
`;
